import { FC, useEffect, useRef, useState } from 'react';

interface OwnerPanelProps {
  onClose: () => void;
}

const mainPoints = [
  'Shifting is allowed only after the deposit and first month’s rent are transferred.',
  'Steps are taken to ensure rent is transferred between the 1st–5th of every month, or by the agreed monthly date.',
  'Pest control and basic cleaning is arranged and fully covered by us before handover to the tenant.',
  'Key acknowledgement is provided if keys are collected.',
  'Complete flat inspection is done before the tenant vacates.',
  'We aim to re-rent the flat in the same condition as received, with inspection support before the next handover.',
];

const supportPoints = [
  'Fully online process with minimal calling.',
  'Documentation handling & coordination for Agreement, Police Verification, Payments and Society NOC, if applicable.',
  'Guided support and practical tips to help reduce unnecessary expenses.',
];

const OwnerPanel: FC<OwnerPanelProps> = ({ onClose }) => {
  const backBtnRef = useRef<HTMLButtonElement | null>(null);
  const [showFloat, setShowFloat] = useState(false);
  const [showReRental, setShowReRental] = useState(false);

  useEffect(() => {
    const btn = backBtnRef.current;
    if (!btn) return;
    let container: HTMLElement | null = btn.parentElement;
    while (container && !(container.scrollHeight > container.clientHeight)) {
      container = container.parentElement;
    }
    if (!container) return;

    const update = () => {
      const rect = btn.getBoundingClientRect();
      const cRect = container!.getBoundingClientRect();
      setShowFloat(rect.bottom < cRect.top + 8);
    };

    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    return () => {
      container!.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const handleContactClick = () => {
    onClose();
    window.setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 160);
  };

  return (
    <div className="px-4 pt-8 pb-28 md:px-12 md:py-10">
      {/* Floating X — mobile only, appears once scrolled past the top Back button */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Back"
        className={`fixed top-4 right-4 z-[60] w-9 h-9 flex items-center justify-center rounded-full bg-foreground-700/75 text-background-50 backdrop-blur-sm transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] cursor-pointer ${
          showFloat ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-75 -translate-y-2 pointer-events-none'
        }`}
      >
        <i className="ri-close-line text-lg" />
      </button>

      {/* Back Button */}
      <button
        ref={backBtnRef}
        onClick={onClose}
        className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors duration-200 mb-8 whitespace-nowrap cursor-pointer"
      >
        <i className="ri-arrow-left-line text-lg" />
        <span>Back</span>
      </button>

      {/* Label */}
      <p className="text-[0.7rem] tracking-[0.3em] uppercase text-accent-500 mb-6">
        BENEFITS FOR OWNERS
      </p>

      {/* Main Service Points */}
      <div className="space-y-4 mb-10">
        {mainPoints.map((point, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-emerald-600 border-0 mt-0.5">
              <i className="ri-check-line text-xs text-white" />
            </div>
            <p className="text-foreground-800 leading-relaxed">{point}</p>
          </div>
        ))}
      </div>

      {/* Common Support Points */}
      <div className="space-y-4 mb-10">
        {supportPoints.map((point, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-sky-50 mt-0.5">
              <i className="ri-verified-badge-fill text-xs text-sky-600" />
            </div>
            <p className="text-foreground-800 leading-relaxed">{point}</p>
          </div>
        ))}

        {/* 6-Month Priority Re-Rental Protection */}
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-sky-50 mt-0.5">
            <i className="ri-verified-badge-fill text-xs text-sky-600" />
          </div>
          <div>
            <p className="text-foreground-800 leading-relaxed">
              <span className="font-medium">6 Month Priority Re-Rental Protection</span>
              <button
                type="button"
                onClick={() => setShowReRental((v) => !v)}
                className="italic text-xs text-foreground-500 hover:text-foreground-700 transition-colors duration-200 ml-1.5 cursor-pointer"
              >
                {showReRental ? 'read less..' : 'read more..'}
              </button>
            </p>
            {showReRental && (
              <p className="text-foreground-800 leading-relaxed mt-2">
                If your tenancy is to end unexpectedly within the first 6 months of the agreement due to a breach by the tenant or unlawful use of the property, SettleSpace provides priority re-rental assistance at no additional service charge, helping you find a suitable new tenant as quickly as possible.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Charges Section */}
      <div className="bg-background-100/70 rounded-lg px-6 py-5 mb-4 border border-background-200/60">
        <div className="flex items-center gap-2 mb-1">
          <i className="ri-bill-line text-foreground-500" />
          <p className="text-[0.7rem] tracking-[0.25em] uppercase text-foreground-500">Charges & Service Fee</p>
        </div>
        <p className="text-foreground-500 text-xs mb-4">Transparent pricing, no surprises.</p>
        <div className="space-y-2.5">
          <p className="text-foreground-800 leading-relaxed text-sm">
            No GST added. No hidden charges.
          </p>
          <p className="text-foreground-800 leading-relaxed text-sm">
            One month's rent as service fee for 1, 2 or 3 years of tenure.
          </p>
          <p className="text-foreground-800 leading-relaxed text-sm">
            To be cleared after documentation is complete and prior to move-in.
          </p>
        </div>
      </div>

      {/* Highlighted Note */}
      <div className="bg-background-100/70 rounded-lg px-6 py-5 mb-10 border border-background-200/60">
        <div className="flex items-center gap-2 mb-1">
          <i className="ri-gift-line text-foreground-500" />
          <p className="text-[0.7rem] tracking-[0.25em] uppercase text-foreground-500">Repeat Client Benefit</p>
        </div>
        <p className="text-foreground-700 leading-relaxed text-sm">
          Re-rent with us and automatically get 35% off your next service fee.
        </p>
      </div>

      {/* CTA Buttons */}
      {/* Mobile — native WhatsApp + call actions kept 100% intact */}
      <div className="md:hidden flex flex-col items-center gap-2">
        <a
          href="https://wa.me/918591019785"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-primary-500 text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
        >
          <i className="ri-whatsapp-line text-base" />
          List Your Property
        </a>
        <span className="text-xs text-foreground-400">or</span>
        <a
          href="tel:+918591019785"
          className="inline-flex items-center justify-center gap-2 bg-background-50 text-foreground-800 rounded-full px-6 py-2 text-sm font-medium border border-foreground-200/60 hover:border-foreground-300 hover:bg-background-100 transition-all duration-200 whitespace-nowrap"
        >
          <i className="ri-phone-line text-base" />
          Call Us
        </a>
      </div>

      {/* Desktop — no WhatsApp icon, scrolls to the Contact Us form, hover lift */}
      <div className="hidden md:flex justify-center">
        <button
          type="button"
          onClick={handleContactClick}
          className="group inline-flex items-center justify-center gap-2 bg-primary-500 text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-primary-600 transition-all duration-300 ease-out hover:-translate-y-1.5 hover:scale-[1.05] active:translate-y-0 active:scale-100 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-base transition-transform duration-300 ease-out group-hover:translate-x-1 group-hover:scale-110" />
          List Your Property
        </button>
      </div>
    </div>
  );
};

export default OwnerPanel;